import { useEffect, useState, type FormEvent } from 'react';
import type { SessionInfo } from '../shared/model';
import { Brand } from './identity';

async function post(path: string, csrf: string, body: unknown) {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': csrf },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || 'Não foi possível concluir. Solicite um novo link.');
  return result;
}
export function EmailLoginRequest({ session }: { session: SessionInfo }) {
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [error, setError] = useState('');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get('email') ?? '').trim();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      setMessage((await post('/api/auth/email/request', session.csrf, { email })).message);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-labelledby="email-login-heading">
      <h2 id="email-login-heading">Entrar por e-mail</h2>
      <p>
        Use o endereço convidado pelo administrador. Solicite o link no navegador em que pretende
        entrar e abra o e-mail nesse mesmo navegador.
      </p>
      {session.emailLogin?.available ? (
        <form onSubmit={submit}>
          <label className="field">
            <span>E-mail convidado</span>
            <input
              name="email"
              type="email"
              autoComplete="email"
              maxLength={254}
              required
              disabled={busy}
            />
          </label>
          <button className="primary" disabled={busy}>
            {busy ? 'Solicitando…' : 'Receber link de acesso'}
          </button>
        </form>
      ) : (
        <p className="meta-message">
          O acesso por e-mail está preparado e aguarda a configuração do remetente. Use o acesso
          disponível ou fale com o administrador.
        </p>
      )}
      {message && (
        <p role="status" className="meta-message">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="meta-message error">
          {error}
        </p>
      )}
    </section>
  );
}
export function EmailLoginLink({ session }: { session: SessionInfo }) {
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get('token') ?? '');
  useEffect(() => {
    window.history.replaceState(null, '', '/auth/email');
  }, []);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const sameOrigin = !session.loginOrigin || location.origin === session.loginOrigin;
  async function confirm() {
    setBusy(true);
    setError('');
    try {
      await post('/api/auth/email/consume', session.csrf, { token });
      location.replace('/#overview');
    } catch (reason) {
      setError((reason as Error).message);
      setBusy(false);
    }
  }
  return (
    <main className="meta-login">
      <section className="panel login-card">
        <div className="brand">
          <Brand />
        </div>
        <h1>Confirmar acesso ao FLG</h1>
        <p>
          Confirme para entrar com o e-mail convidado. O link funciona por dez minutos no navegador
          em que foi solicitado. Abrir esta página não confirma a entrada.
        </p>
        {error && (
          <p role="alert" className="meta-message error">
            {error}
          </p>
        )}
        {!token || !sameOrigin ? (
          <p role="alert" className="meta-message error">
            Solicite outro link neste navegador e abra o endereço seguro do FLG.
          </p>
        ) : (
          <button
            className="primary"
            onClick={() => void confirm()}
            disabled={busy || !session.emailLogin?.available}
          >
            {busy ? 'Confirmando…' : 'Confirmar entrada por e-mail'}
          </button>
        )}
        <p>
          <a className="text-button" href="/">
            Voltar e solicitar outro link
          </a>
        </p>
        <p className="fine-print">
          O acesso editorial não conecta contas da Meta e não autoriza publicações.
        </p>
      </section>
    </main>
  );
}
